import { useState, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { money, fmtDate } from "@/lib/format";
import { linkSupplierProduct, unlinkSupplierProduct } from "@/lib/api/supplier-products";
import { canManageSuppliers } from "@/lib/permissions";

interface Supplier {
  id: string;
  name: string;
  code: string | null;
  status: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  payment_terms: string | null;
  lead_time_days: number | null;
  notes: string | null;
  currency: string;
  tax_id: string | null;
  is_preferred: boolean;
}

interface SupplierProduct {
  id: string;
  product_id: string;
  supplier_sku: string | null;
  supplier_price: number | null;
  min_order_qty: number | null;
  order_multiple: number | null;
  lead_time_days: number | null;
  is_preferred: boolean;
  products?: { name: string; sku: string } | null;
}

interface PoRow {
  id: string;
  po_number: string;
  status: string;
  total: number | null;
  order_date: string | null;
  expected_date: string | null;
}

interface PoItemAgg { qty_ordered: number; qty_received: number; }

interface ReceiptRow { purchase_order_id: string; received_at: string | null; }

interface ProductOption { id: string; name: string; sku: string; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageSuppliers(role);
  const orgCurrency = org?.currency || "USD";

  const [linkOpen, setLinkOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SupplierProduct | null>(null);
  const [form, setForm] = useState({ product_id: "", variant_id: "", supplier_sku: "", supplier_price: "", min_order_qty: "", order_multiple: "", lead_time_days: "", is_preferred: false });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "", code: "", status: "active", contact_name: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "",
    payment_terms: "", lead_time_days: "", notes: "", currency: orgCurrency, tax_id: "", is_preferred: false,
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["supplier-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [supplier, links, pos, products, variants] = await Promise.all([
        supabase.from("suppliers").select("*").eq("org_id", org!.id).eq("id", id).maybeSingle(),
        supabase.from("supplier_products").select("id, product_id, supplier_sku, supplier_price, min_order_qty, order_multiple, lead_time_days, is_preferred, products(name, sku)").eq("org_id", org!.id).eq("supplier_id", id),
        supabase.from("purchase_orders").select("id, po_number, status, total, order_date, expected_date").eq("org_id", org!.id).eq("supplier_id", id).order("created_at", { ascending: false }),
        supabase.from("products").select("id, name, sku").eq("org_id", org!.id).order("name"),
        supabase.from("variants").select("id, product_id, sku, option_values").eq("org_id", org!.id).order("position"),
      ]);
      const posList = (pos.data ?? []) as PoRow[];
      let items: PoItemAgg[] = [];
      let receipts: ReceiptRow[] = [];
      if (posList.length > 0) {
        const [itemRes, receiptRes] = await Promise.all([
          supabase
            .from("purchase_order_items")
            .select("qty_ordered, qty_received")
            .eq("org_id", org!.id)
            .in("purchase_order_id", posList.map((p) => p.id)),
          supabase
            .from("purchase_receipts")
            .select("purchase_order_id, received_at")
            .eq("org_id", org!.id)
            .in("purchase_order_id", posList.map((p) => p.id)),
        ]);
        items = (itemRes.data ?? []) as PoItemAgg[];
        receipts = (receiptRes.data ?? []) as ReceiptRow[];
      }
      return {
        supplier: (supplier.data ?? null) as Supplier | null,
        links: (links.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as SupplierProduct[],
        pos: posList,
        products: (products.data ?? []) as ProductOption[],
        variants: (variants.data ?? []) as { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null }[],
        items,
        receipts,
      };
    },
  });

  const handleLink = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.product_id) {
      setError("Select a product.");
      return;
    }
    setSaving(true);
    try {
      await linkSupplierProduct(org!.id, {
        productId: form.product_id,
        supplierId: id!,
        variantId: form.variant_id || null,
        supplierSku: form.supplier_sku || null,
        supplierPrice: form.supplier_price ? Number(form.supplier_price) : null,
        minOrderQty: form.min_order_qty ? Number(form.min_order_qty) : 1,
        orderMultiple: form.order_multiple ? Number(form.order_multiple) : null,
        leadTimeDays: form.lead_time_days ? Number(form.lead_time_days) : null,
        isPreferred: form.is_preferred,
        currency: data?.supplier?.currency ?? orgCurrency,
      });
    } catch (err) {
      setSaving(false);
      setError((err as Error).message);
      return;
    }
    setSaving(false);
    setLinkOpen(false);
    setForm({ product_id: "", variant_id: "", supplier_sku: "", supplier_price: "", min_order_qty: "", order_multiple: "", lead_time_days: "", is_preferred: false });
    queryClient.invalidateQueries({ queryKey: ["supplier-detail", org?.id, id] });
  };

  const removeLink = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await unlinkSupplierProduct(org!.id, deleteTarget.id);
    } catch (err) {
      setSaving(false);
      setError((err as Error).message);
      return;
    }
    setSaving(false);
    setDeleteTarget(null);
    queryClient.invalidateQueries({ queryKey: ["supplier-detail", org?.id, id] });
  };

  const openEdit = () => {
    if (!data?.supplier) return;
    const s = data.supplier;
    setEditForm({
      name: s.name,
      code: s.code ?? "",
      status: s.status,
      contact_name: s.contact_name ?? "",
      email: s.email ?? "",
      phone: s.phone ?? "",
      address_line1: s.address_line1 ?? "",
      address_line2: s.address_line2 ?? "",
      city: s.city ?? "",
      state: s.state ?? "",
      postal_code: s.postal_code ?? "",
      country: s.country ?? "",
      payment_terms: s.payment_terms ?? "",
      lead_time_days: s.lead_time_days != null ? String(s.lead_time_days) : "",
      notes: s.notes ?? "",
      currency: s.currency ?? orgCurrency,
      tax_id: s.tax_id ?? "",
      is_preferred: s.is_preferred ?? false,
    });
    setError("");
    setEditOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: updErr } = await supabase
      .from("suppliers")
      .update({
        name: editForm.name,
        code: editForm.code || null,
        status: editForm.status,
        contact_name: editForm.contact_name || null,
        email: editForm.email || null,
        phone: editForm.phone || null,
        address_line1: editForm.address_line1 || null,
        address_line2: editForm.address_line2 || null,
        city: editForm.city || null,
        state: editForm.state || null,
        postal_code: editForm.postal_code || null,
        country: editForm.country || null,
        payment_terms: editForm.payment_terms || null,
        lead_time_days: editForm.lead_time_days ? Number(editForm.lead_time_days) : null,
        notes: editForm.notes || null,
        currency: editForm.currency || orgCurrency,
        tax_id: editForm.tax_id || null,
        is_preferred: editForm.is_preferred,
      })
      .eq("id", id);
    setSaving(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ["supplier-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["suppliers", org?.id] });
  };

  const toggleStatus = async () => {
    if (!data?.supplier) return;
    const next = data.supplier.status === "active" ? "inactive" : "active";
    setSaving(true);
    await supabase.from("suppliers").update({ status: next }).eq("id", id);
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["supplier-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["suppliers", org?.id] });
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.supplier) {
    return (
      <div>
        <div className="mb-6">
          <button onClick={() => navigate("/suppliers")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back to suppliers
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this supplier.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const s = data.supplier;
  const addressParts = [s.address_line1, s.address_line2, s.city, s.state, s.postal_code, s.country].filter(Boolean);

  const totalSpend = data.pos.reduce((sum, p) => sum + Number(p.total ?? 0), 0);
  const openValue = data.pos.filter((p) => ["pending", "partial"].includes(p.status)).reduce((sum, p) => sum + Number(p.total ?? 0), 0);
  const ordered = (data.items ?? []).reduce((sum, i) => sum + Number(i.qty_ordered ?? 0), 0);
  const received = (data.items ?? []).reduce((sum, i) => sum + Number(i.qty_received ?? 0), 0);
  const fillRate = ordered > 0 ? Math.round((received / ordered) * 100) : null;

  // Delivery performance: actual measured lead time + on-time rate from history
  const firstReceiptByPo: Record<string, string> = {};
  for (const r of data.receipts) {
    if (!r.received_at) continue;
    const cur = firstReceiptByPo[r.purchase_order_id];
    if (!cur || r.received_at < cur) firstReceiptByPo[r.purchase_order_id] = r.received_at;
  }
  let leadSum = 0;
  let leadCount = 0;
  let onTime = 0;
  let onTimeCount = 0;
  for (const po of data.pos) {
    const receivedAt = firstReceiptByPo[po.id];
    if (receivedAt && po.order_date) {
      const days = (new Date(receivedAt).getTime() - new Date(po.order_date).getTime()) / 86400000;
      if (days >= 0) {
        leadSum += days;
        leadCount += 1;
      }
    }
    if (receivedAt && po.expected_date) {
      onTimeCount += 1;
      if (new Date(receivedAt) <= new Date(po.expected_date)) onTime += 1;
    }
  }
  const avgLeadTime = leadCount > 0 ? Math.round(leadSum / leadCount) : null;
  const onTimeRate = onTimeCount > 0 ? Math.round((onTime / onTimeCount) * 100) : null;

  return (
    <div>
      <div className="mb-6">
        <button onClick={() => navigate("/suppliers")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
          <i className="ri-arrow-left-line" /> Back to suppliers
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <i className="ri-truck-line text-emerald-600 text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{s.name}</h1>
                <StatusBadge status={s.status} />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {s.code ? <span className="font-mono mr-3">{s.code}</span> : null}
                {s.contact_name ?? "—"}
                {s.email ? ` · ${s.email}` : ""}
                {s.phone ? ` · ${s.phone}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase tracking-wider">Payment terms</p>
              <p className="text-sm font-semibold text-slate-800 mt-1">{s.payment_terms ?? "—"}</p>
              {s.lead_time_days != null && (
                <p className="text-xs text-slate-400 mt-1.5">{s.lead_time_days} day lead time</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canManage && (
                <button onClick={openEdit} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                  <i className="ri-pencil-line" /> Edit
                </button>
              )}
              {canManage && (
                <button
                  onClick={toggleStatus}
                  disabled={saving}
                  className={`px-4 py-2 border text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 disabled:opacity-60 ${
                    s.status === "active"
                      ? "bg-white border-slate-200 hover:bg-red-50 hover:text-red-600 text-slate-700"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
                  }`}
                >
                  <i className={s.status === "active" ? "ri-user-unfollow-line" : "ri-user-follow-line"} />
                  {s.status === "active" ? "Deactivate" : "Activate"}
                </button>
              )}
            </div>
          </div>
        </div>

        {(addressParts.length > 0 || s.notes) && (
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            {addressParts.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Address</p>
                <p className="text-sm text-slate-700">{addressParts.join(", ")}</p>
              </div>
            )}
            {s.notes && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Notes</p>
                <p className="text-sm text-slate-700">{s.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">Lifetime spend</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{money(totalSpend)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">Open orders</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{money(openValue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">Fill rate</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{fillRate != null ? `${fillRate}%` : "—"}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">Avg lead time</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{avgLeadTime != null ? `${avgLeadTime}d` : "—"}</p>
          {s.lead_time_days != null && avgLeadTime != null && (
            <p className={`text-[11px] mt-0.5 ${avgLeadTime <= s.lead_time_days ? "text-emerald-600" : "text-amber-600"}`}>
              {s.lead_time_days}d expected
            </p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">On-time rate</p>
          <p className={`text-lg font-bold mt-1 ${onTimeRate == null ? "text-slate-900" : onTimeRate >= 90 ? "text-emerald-700" : onTimeRate >= 70 ? "text-amber-600" : "text-red-600"}`}>
            {onTimeRate != null ? `${onTimeRate}%` : "—"}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-400">Total orders</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{data.pos.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Supplied products</h3>
            {canManage && (
              <button onClick={() => { setError(""); setLinkOpen(true); }} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Link product
              </button>
            )}
          </div>
          {data.links.length === 0 ? (
            <EmptyState title="No products linked" description="Link products this supplier provides." />
          ) : (
            <div className="divide-y divide-slate-50">
              {data.links.map((l) => (
                <div key={l.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/products/${l.product_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 truncate">
                        {l.products?.name ?? "—"}
                      </Link>
                      {l.is_preferred && <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full shrink-0">Preferred</span>}
                    </div>
                    <p className="text-xs text-slate-400 font-mono">{l.products?.sku ?? "—"}{l.supplier_sku ? ` · ${l.supplier_sku}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">{l.supplier_price != null ? money(l.supplier_price) : "—"}</p>
                      <p className="text-xs text-slate-400">
                        {l.min_order_qty ? `min ${l.min_order_qty}` : ""}
                        {l.order_multiple && l.order_multiple > 1 ? ` · ×${l.order_multiple}` : ""}
                        {l.lead_time_days ? ` · ${l.lead_time_days}d` : ""}
                      </p>
                    </div>
                    {canManage && (
                      <button onClick={() => setDeleteTarget(l)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500">
                        <i className="ri-close-line" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Purchase orders</h3>
          </div>
          {data.pos.length === 0 ? (
            <EmptyState title="No purchase orders" description="Purchase orders from this supplier will appear here." />
          ) : (
            <div className="divide-y divide-slate-50">
              {data.pos.map((po) => (
                <Link key={po.id} to={`/purchase-orders/${po.id}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 font-mono">{po.po_number}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(po.order_date)}{po.expected_date ? ` · due ${fmtDate(po.expected_date)}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-slate-800">{money(po.total)}</span>
                    <StatusBadge status={po.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal open={linkOpen} onClose={() => setLinkOpen(false)} title="Link product"
        footer={
          <>
            <button onClick={() => setLinkOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleLink} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Linking..." : "Link Product"}
            </button>
          </>
        }
      >
        <form onSubmit={handleLink} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Product *</label>
            <select required value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value, variant_id: "" }))} className={inputCls}>
              <option value="">Select product...</option>
              {data.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
            </select>
          </div>
          {(data.variants.filter((v) => v.product_id === form.product_id).length > 0) && (
            <div>
              <label className={labelCls}>Variant</label>
              <select value={form.variant_id} onChange={(e) => setForm((f) => ({ ...f, variant_id: e.target.value }))} className={inputCls}>
                <option value="">All variants</option>
                {data.variants.filter((v) => v.product_id === form.product_id).map((v) => (
                  <option key={v.id} value={v.id}>{v.sku}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Link to a specific variant, or leave on "All variants" for the whole product.</p>
            </div>
          )}
          <div>
            <label className={labelCls}>Supplier SKU</label>
            <input value={form.supplier_sku} onChange={(e) => setForm((f) => ({ ...f, supplier_sku: e.target.value }))} className={inputCls} placeholder="SUP-001" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Supplier price</label>
              <input type="number" step="0.01" min="0" value={form.supplier_price} onChange={(e) => setForm((f) => ({ ...f, supplier_price: e.target.value }))} className={inputCls} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>Min order qty</label>
              <input type="number" min="0" value={form.min_order_qty} onChange={(e) => setForm((f) => ({ ...f, min_order_qty: e.target.value }))} className={inputCls} placeholder="1" />
            </div>
            <div>
              <label className={labelCls}>Order multiple</label>
              <input type="number" min="0" value={form.order_multiple} onChange={(e) => setForm((f) => ({ ...f, order_multiple: e.target.value }))} className={inputCls} placeholder="1" />
              <p className="text-xs text-slate-400 mt-1">Order in multiples of this many.</p>
            </div>
            <div>
              <label className={labelCls}>Lead time (days)</label>
              <input type="number" min="0" value={form.lead_time_days} onChange={(e) => setForm((f) => ({ ...f, lead_time_days: e.target.value }))} className={inputCls} placeholder="7" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.is_preferred} onChange={(e) => setForm((f) => ({ ...f, is_preferred: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
            Preferred supplier for this product
          </label>
        </form>
      </Modal>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit supplier"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input required value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input value={editForm.code} onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Payment terms</label>
              <input value={editForm.payment_terms} onChange={(e) => setEditForm((f) => ({ ...f, payment_terms: e.target.value }))} className={inputCls} placeholder="Net 30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Contact name</label>
              <input value={editForm.contact_name} onChange={(e) => setEditForm((f) => ({ ...f, contact_name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Lead time (days)</label>
              <input type="number" min="0" value={editForm.lead_time_days} onChange={(e) => setEditForm((f) => ({ ...f, lead_time_days: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Address line 1</label>
              <input value={editForm.address_line1} onChange={(e) => setEditForm((f) => ({ ...f, address_line1: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address line 2</label>
              <input value={editForm.address_line2} onChange={(e) => setEditForm((f) => ({ ...f, address_line2: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>City</label>
              <input value={editForm.city} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>State</label>
              <input value={editForm.state} onChange={(e) => setEditForm((f) => ({ ...f, state: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Postal code</label>
              <input value={editForm.postal_code} onChange={(e) => setEditForm((f) => ({ ...f, postal_code: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Country</label>
              <input value={editForm.country} onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <select value={editForm.currency} onChange={(e) => setEditForm((f) => ({ ...f, currency: e.target.value }))} className={inputCls}>
                {["USD", "EUR", "GBP", "AUD", "CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tax ID / VAT</label>
              <input value={editForm.tax_id} onChange={(e) => setEditForm((f) => ({ ...f, tax_id: e.target.value }))} className={inputCls} placeholder="e.g. ABN 12 345 678 901" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={editForm.is_preferred} onChange={(e) => setEditForm((f) => ({ ...f, is_preferred: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
            Preferred supplier
          </label>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} rows={3} maxLength={500} />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Unlink product"
        message={`Remove "${deleteTarget?.products?.name ?? ""}" from this supplier?`}
        confirmLabel="Unlink"
        loading={saving}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={removeLink}
      />
    </div>
  );
}